import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ImageOff } from "lucide-react";
import { Button } from "../ui/button";
import { DataTable } from "../shared/DataTable";
import { Pagination } from "../shared/Pagination";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { Input } from "../ui/input";
import { DailyMenuFormDialog } from "./DailyMenuFormDialog";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { formatDate } from "../../lib/utils";
import { useListState } from "../../hooks/useListState";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;
const LIMIT = 20;

function MenuThumb({ image, testId }) {
  const [broken, setBroken] = useState(false);
  if (!image || broken) {
    return (
      <div className="flex h-12 w-16 items-center justify-center rounded-md border border-dashed border-border text-text-muted">
        <ImageOff size={16} strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <img
      src={`${API_BASE}/daily-menu/images/${image}`} alt="Menu"
      className="h-12 w-16 rounded-md border border-border object-cover"
      onError={() => setBroken(true)}
      data-testid={testId}
    />
  );
}

export function DailyMenuTab() {
  const [state, setState] = useState({ loading: true, rows: [], total: 0 });
  const [page, setPage] = useListState("catalog.dailyMenu.page", 0);
  const [dateFilter, setDateFilter] = useListState("catalog.dailyMenu.dateFilter", "");
  const [dialogMenu, setDialogMenu] = useState(undefined);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = () => {
    setState((s) => ({ ...s, loading: true }));
    apiClient.get("/daily-menu", { params: { date: dateFilter || undefined, limit: LIMIT, offset: page * LIMIT } })
      .then(({ data }) => setState({ loading: false, rows: data.items, total: data.total }))
      .catch((error) => { toast.error(getApiErrorMessage(error, "Could not load the daily menu.")); setState((s) => ({ ...s, loading: false })); });
  };

  useEffect(refresh, [page, dateFilter]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/daily-menu/${deleteTarget.id}`);
      toast.success("Daily menu entry deleted");
      setDeleteTarget(null);
      refresh();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not delete this entry."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-4" data-testid="daily-menu-tab">
      <div className="mb-4 flex items-center justify-between gap-3">
        <Input
          type="date"
          value={dateFilter}
          onChange={(e) => { setDateFilter(e.target.value); setPage(0); }}
          className="max-w-[180px]"
          data-testid="daily-menu-date-filter"
        />
        <Button size="sm" onClick={() => setDialogMenu(null)} data-testid="daily-menu-new-button">
          <Plus size={15} strokeWidth={1.5} /> New Entry
        </Button>
      </div>

      <DataTable
        testId="daily-menu-table"
        loading={state.loading}
        rows={state.rows}
        emptyTitle="No daily menu entries yet"
        columns={[
          {
            key: "image", header: "Photo", leading: true, render: (r) => <MenuThumb image={r.image} testId={`daily-menu-thumb-${r.id}`} />,
          },
          { key: "date", header: "Date", primary: true, render: (r) => formatDate(r.date) },
          { key: "comment", header: "Comment", subtitle: true, render: (r) => <span className="line-clamp-2">{r.comment || "—"}</span> },
          {
            key: "actions", header: "", render: (r) => (
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="icon" onClick={() => setDialogMenu(r)} data-testid={`daily-menu-edit-${r.id}`}>
                  <Pencil size={14} strokeWidth={1.5} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(r)} data-testid={`daily-menu-delete-${r.id}`}>
                  <Trash2 size={14} strokeWidth={1.5} className="text-danger" />
                </Button>
              </div>
            ),
          },
        ]}
      />
      <Pagination page={page} onPageChange={setPage} total={state.total} pageSize={LIMIT} testId="daily-menu-pagination" />

      <DailyMenuFormDialog
        open={dialogMenu !== undefined} onOpenChange={(v) => !v && setDialogMenu(undefined)}
        menu={dialogMenu} onSaved={() => { setDialogMenu(undefined); refresh(); }}
      />
      <ConfirmDialog
        open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete this entry?" description={`The daily menu entry for ${deleteTarget ? formatDate(deleteTarget.date) : ""} will be permanently deleted.`}
        confirmLabel="Delete Entry" variant="danger" loading={deleting} onConfirm={handleDelete}
        testId="daily-menu-delete-confirm-dialog"
      />
    </div>
  );
}
